import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { addressLines } from "./addressSuggestion";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.58);
`;

const Dialog = styled.div`
  width: min(100%, 560px);
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.3);
  padding: 24px;
`;

const Title = styled.h2`
  margin: 0;
  color: #172033;
  font-size: 1.35rem;
`;

const Intro = styled.p`
  margin: 8px 0 20px;
  color: #5f6b7a;
  line-height: 1.5;
`;

const Choices = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const AddressCard = styled.div`
  border: 1px solid ${({ $suggested }) => ($suggested ? "#8b7cf6" : "#d9dee8")};
  border-radius: 10px;
  padding: 14px;
  background: ${({ $suggested }) => ($suggested ? "#f7f5ff" : "#fafbfc")};
`;

const AddressLabel = styled.strong`
  display: block;
  margin-bottom: 8px;
  color: #30384a;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const AddressLine = styled.div`
  color: #172033;
  line-height: 1.45;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;

  @media (max-width: 540px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button`
  min-height: 44px;
  border-radius: 8px;
  padding: 10px 16px;
  border: 1px solid ${({ $primary }) => ($primary ? "#5543d9" : "#cbd2de")};
  background: ${({ $primary }) => ($primary ? "#5543d9" : "#fff")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#30384a")};
  cursor: pointer;
  font-weight: 650;

  &:focus-visible {
    outline: 3px solid rgba(85, 67, 217, 0.28);
    outline-offset: 2px;
  }
`;

const AddressSuggestionDialog = ({ suggestion, onKeepEntered, onUseSuggested }) => {
  const primaryButtonRef = useRef(null);

  useEffect(() => {
    primaryButtonRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onKeepEntered();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onKeepEntered]);

  return (
    <Overlay role="presentation">
      <Dialog role="dialog" aria-modal="true" aria-labelledby="address-suggestion-title">
        <Title id="address-suggestion-title">Confirm your address</Title>
        <Intro>
          We found a standardized address. You can use it or keep the address you entered.
        </Intro>
        <Choices>
          <AddressCard>
            <AddressLabel>Your entry</AddressLabel>
            {addressLines(suggestion.entered).map((line) => (
              <AddressLine key={line}>{line}</AddressLine>
            ))}
          </AddressCard>
          <AddressCard $suggested>
            <AddressLabel>Suggested address</AddressLabel>
            {addressLines(suggestion.suggested).map((line) => (
              <AddressLine key={line}>{line}</AddressLine>
            ))}
          </AddressCard>
        </Choices>
        <Actions>
          <Button type="button" onClick={onKeepEntered}>Keep entered address</Button>
          <Button
            ref={primaryButtonRef}
            type="button"
            $primary
            onClick={onUseSuggested}
          >
            Use suggested address
          </Button>
        </Actions>
      </Dialog>
    </Overlay>
  );
};

export default AddressSuggestionDialog;
